import _ from 'underscore';

import EntityStore from 'stores/EntityStore';

import {TreeItem, TreeItemState, TreeItemStateMap} from './SelectTreeConstants';

export function getAllDisplayedIds(
  itemStateMap: TreeItemStateMap,
  rootItems: Array<TreeItem>
): Array<string> {
  let allDisplayedIds: Array<string> = [];
  rootItems.forEach((rootItem) => {
    allDisplayedIds.push(rootItem.id);
    if (Object.keys(itemStateMap).includes(rootItem.id)) {
      const itemState = itemStateMap[rootItem.id];
      allDisplayedIds = addContainingIds(allDisplayedIds, itemState, itemStateMap);
    }
  });
  return allDisplayedIds;
}

function addContainingIds(
  idList: Array<string>,
  itemState: TreeItemState,
  itemStateMap: TreeItemStateMap
): Array<string> {
  if (itemState.isExpanded) {
    itemState.childItems.forEach((childItem) => {
      const childItemId = childItem.id;
      if (childItemId != null) {
        idList.push(childItemId);
        idList = addContainingIds(idList, itemStateMap[childItemId], itemStateMap);
      }
    });
    itemState.leafItems.forEach((leafItem) => {
      const leafItemId = leafItem.id;
      if (leafItemId != null) {
        idList.push(leafItemId);
      }
    });
  }
  return idList;
}

export function findShiftSelectedIds(
  nextSelectedIds: Array<string>,
  allDisplayedIds: Array<string>,
  currentSelectedId: string,
  previousSelectedIds: Array<string>,
  previousClickedIds: Array<string>
): Array<string> {
  const isUnclick = previousSelectedIds.includes(currentSelectedId);
  const [first, last] = getShiftClickEndpoints(
    nextSelectedIds,
    allDisplayedIds,
    currentSelectedId,
    previousClickedIds
  );
  if (first === last) {
    return [first];
  }

  let inBetween = false;
  let shiftSelectedIds = [...nextSelectedIds];
  if (isUnclick) {
    shiftSelectedIds.push(currentSelectedId); // currentSelectedId stays selected upon shift-click
    allDisplayedIds.forEach((currentId) => {
      if (currentId === first || currentId === last) {
        inBetween = !inBetween;
        if (currentId !== currentSelectedId && shiftSelectedIds.includes(currentId)) {
          shiftSelectedIds = shiftSelectedIds.filter((id) => id !== currentId);
        }
      } else if (inBetween && shiftSelectedIds.includes(currentId)) {
        shiftSelectedIds = shiftSelectedIds.filter((id) => id !== currentId);
      }
    });
  } else {
    allDisplayedIds.forEach((currentId) => {
      if (currentId === first || currentId === last) {
        inBetween = !inBetween;
        if (!shiftSelectedIds.includes(currentId)) {
          shiftSelectedIds.push(currentId);
        }
      } else if (inBetween && !shiftSelectedIds.includes(currentId)) {
        shiftSelectedIds.push(currentId);
      }
    });
  }
  return shiftSelectedIds;
}

function getShiftClickEndpoints(
  selectedIds: Array<string>,
  allDisplayedIds: Array<string>,
  currentSelectedId: string,
  previousClickedIds: Array<string>
): Array<string> {
  let previousSelectedId: string;
  if (previousClickedIds.length > 0) {
    let foundValidPrevClick = false;
    let prevClickNowSelected: string = '';
    let idx = previousClickedIds.length - 1;
    while (!foundValidPrevClick && idx >= 0) {
      prevClickNowSelected = previousClickedIds[idx];
      if (selectedIds.includes(prevClickNowSelected)) {
        foundValidPrevClick = true;
      } else {
        idx -= 1;
      }
    }
    previousSelectedId = !foundValidPrevClick ? currentSelectedId : prevClickNowSelected;
  } else {
    previousSelectedId = currentSelectedId;
  }

  let found = false;
  let first: string = '';
  let last: string = '';
  allDisplayedIds.forEach((currentId) => {
    if (currentId === previousSelectedId) {
      if (!found) {
        first = previousSelectedId;
        found = true;
      } else {
        last = previousSelectedId;
      }
    }
    // account for when currentSelectedId === previousSelectedId
    if (currentId === currentSelectedId) {
      if (!found) {
        first = currentSelectedId;
        found = true;
      } else {
        last = currentSelectedId;
      }
    }
  });
  return [first, last];
}

export function sortedRootItems(rootItems: Array<TreeItem>): Array<TreeItem> {
  // Sort by username + folder name. Push current user's folders to top
  return _.chain(rootItems)
    .sortBy((rootItem) => rootItem.name.toLowerCase())
    .sortBy((rootItem) => (rootItem.user !== EntityStore.currentUser()?.handle ? rootItem.user : ''))
    .sortBy((rootItem) => (rootItem.forceToTop ? 0 : 1))
    .value();
}
